/**
 * Transaction Hooks
 *
 * Custom hooks for fetching and managing Plaid transactions.
 *
 * @fileoverview React hooks for transaction operations
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useToast } from '@chakra-ui/react';
import { useSearchParams } from 'react-router-dom';
import { useAuthContext } from '../contexts/AuthContext';
import { useUser } from '../Context/userContext';
import { apiJson } from '../lib/api';
import {
  PlaidTransaction,
  PaginatedTransactionsResponse,
  TransactionFilters,
  TransactionSort,
  TransactionSortColumn,
} from '../types/transactions';
import { toUserMessage } from '../lib/apiError';

/**
 * Options for useTransactions hook
 */
interface UseTransactionsOptions {
  pageSize?: number;
  sortBy?: TransactionSortColumn;
  sortOrder?: 'asc' | 'desc';
  autoFetch?: boolean;
}

/**
 * Return type for useTransactions hook
 */
interface UseTransactionsReturn {
  transactions: PlaidTransaction[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  isLoading: boolean;
  error: string | null;
  filters: TransactionFilters;
  sort: TransactionSort;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  applyFilters: (filters: TransactionFilters) => void;
  updateFilters: (updater: (prev: TransactionFilters) => TransactionFilters) => void;
  applySort: (column: TransactionSortColumn, direction: 'asc' | 'desc') => void;
  toggleSort: (column: TransactionSortColumn) => void;
  clearFilters: () => void;
  refresh: () => Promise<void>;
  updateVendor: (id: string, vendor: string) => Promise<void>;
}

/**
 * Map frontend sort columns to backend sortBy values
 */
function mapSortColumn(column: TransactionSortColumn): 'date' | 'amount' | 'name' {
  switch (column) {
    case 'datetime':
    case 'date':
      return 'date';
    case 'amount':
      return 'amount';
    case 'name':
    case 'merchantName':
    case 'originalDescription':
      return 'name';
    default:
      return 'date';
  }
}

/**
 * Hook for fetching transactions with real numbered pagination, filtering,
 * and sorting. Each page fetch REPLACES `transactions` — unlike the old
 * "Load More" version, pages are not accumulated, so total memory/DOM stays
 * bounded by pageSize regardless of how far the user has paginated.
 */
export function useTransactions(options: UseTransactionsOptions = {}): UseTransactionsReturn {
  const {
    pageSize: initialPageSize = 50,
    sortBy = 'datetime',
    sortOrder = 'desc',
    autoFetch = true,
  } = options;

  const toast = useToast();
  const { session } = useAuthContext();
  const sessionRef = useRef(session);
  useEffect(() => { sessionRef.current = session; });
  const hasSession = !!session?.access_token;
  const { selectedTenant } = useUser();

  // Restore page/filters/sort from the URL on first mount, so a refresh or
  // the browser's back button doesn't silently reset them — see
  // docs/plans/TRANSACTIONS_PAGINATION_PLAN.md, R1-3.
  const [searchParams, setSearchParams] = useSearchParams();

  const [transactions, setTransactions] = useState<PlaidTransaction[]>([]);
  const transactionsRef = useRef(transactions);
  useEffect(() => { transactionsRef.current = transactions; });
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPageState] = useState(() => {
    const p = parseInt(searchParams.get('page') || '', 10);
    return Number.isFinite(p) && p > 0 ? p : 1;
  });
  const [pageSize, setPageSizeState] = useState(() => {
    const s = parseInt(searchParams.get('pageSize') || '', 10);
    return Number.isFinite(s) && s > 0 ? s : initialPageSize;
  });

  const [filters, setFilters] = useState<TransactionFilters>(() => ({
    accountIds: searchParams.get('accountIds')?.split(',').filter(Boolean),
    dateFrom: searchParams.get('dateFrom') || undefined,
    dateTo: searchParams.get('dateTo') || undefined,
  }));
  const [sort, setSort] = useState<TransactionSort>(() => ({
    column: (searchParams.get('sortBy') as TransactionSortColumn) || sortBy,
    direction: searchParams.get('sortOrder') === 'asc' ? 'asc' : sortOrder,
  }));

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  // Mirror page/pageSize/sort and the two most commonly used filters
  // (account, date range) to the URL. Replace, not push, so paginating or
  // typing in a filter doesn't spam browser history. The remaining filter
  // fields (amount range, merchant/category text) are left out of the URL
  // deliberately — they're set from an "advanced filters" panel most users
  // never open, and every additional field here is one more thing that can
  // drift out of sync; account + date range covers the common case.
  useEffect(() => {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('pageSize', String(pageSize));
    params.set('sortBy', sort.column);
    params.set('sortOrder', sort.direction);
    if (filters.accountIds && filters.accountIds.length > 0) {
      params.set('accountIds', filters.accountIds.join(','));
    }
    if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.set('dateTo', filters.dateTo);
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, sort.column, sort.direction, filters.accountIds, filters.dateFrom, filters.dateTo]);

  /**
   * Build query string from current state
   */
  const buildQueryString = useCallback(
    (currentPage: number, currentPageSize: number): string => {
      const params = new URLSearchParams();

      params.append('limit', currentPageSize.toString());
      params.append('offset', ((currentPage - 1) * currentPageSize).toString());
      params.append('sortBy', mapSortColumn(sort.column));
      params.append('sortOrder', sort.direction);

      if (filters.dateFrom) {
        params.append('startDate', filters.dateFrom);
      }
      if (filters.dateTo) {
        params.append('endDate', filters.dateTo);
      }
      if (filters.amountMin !== undefined) {
        params.append('amountMin', filters.amountMin.toString());
      }
      if (filters.amountMax !== undefined) {
        params.append('amountMax', filters.amountMax.toString());
      }
      if (filters.merchantName) {
        params.append('merchantName', filters.merchantName);
      }
      if (filters.category) {
        params.append('category', filters.category);
      }
      if (filters.pfcPrimary) {
        params.append('pfcPrimary', filters.pfcPrimary);
      }
      if (filters.pfcDetailed) {
        params.append('pfcDetailed', filters.pfcDetailed);
      }
      if (filters.transactionClass) {
        params.append('transactionClass', filters.transactionClass);
      }
      if (filters.accountIds && filters.accountIds.length > 0) {
        // Send as comma-separated list (NestJS cannot parse repeated query params properly)
        params.append('accountIds', filters.accountIds.join(','));
      } else if (filters.accountId) {
        params.append('accountId', filters.accountId);
      }

      return params.toString();
    },
    [sort, filters]
  );

  /**
   * Fetch the given page from the API, replacing `transactions`.
   */
  const fetchTransactions = useCallback(
    async (targetPage: number, targetPageSize: number): Promise<void> => {
      if (!sessionRef.current?.access_token || !selectedTenant) {
        setTransactions([]);
        setTotalCount(0);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const queryString = buildQueryString(targetPage, targetPageSize);
        const data: PaginatedTransactionsResponse = await apiJson<PaginatedTransactionsResponse>(
          `/api/plaid-transactions?${queryString}`,
          {
            headers: {
              'x-tenant-id': selectedTenant.id,
            },
          },
          sessionRef.current!.access_token
        );

        setTransactions(data.transactions);
        setTotalCount(data.total);
      } catch (err) {
        setError(toUserMessage(err, "We couldn't load transactions. Please try again."));
      } finally {
        setIsLoading(false);
      }
    },
    [selectedTenant?.id, buildQueryString]
  );

  const setPage = useCallback((next: number): void => {
    setPageState(Math.max(1, next));
  }, []);

  const setPageSize = useCallback((size: number): void => {
    setPageSizeState(size);
    setPageState(1);
  }, []);

  /**
   * Apply filters and reset to page 1
   */
  const applyFilters = useCallback((newFilters: TransactionFilters): void => {
    setFilters(newFilters);
    setPageState(1);
  }, []);

  /**
   * Update filters using functional update pattern (avoids stale closures)
   */
  const updateFilters = useCallback((updater: (prev: TransactionFilters) => TransactionFilters): void => {
    setFilters(updater);
    setPageState(1);
  }, []);

  /**
   * Apply sort and reset to page 1
   */
  const applySort = useCallback(
    (column: TransactionSortColumn, direction: 'asc' | 'desc'): void => {
      setSort({ column, direction });
      setPageState(1);
    },
    []
  );

  /**
   * Toggle sort direction for a column
   */
  const toggleSort = useCallback(
    (column: TransactionSortColumn): void => {
      setSort((prev) => ({
        column,
        direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc',
      }));
      setPageState(1);
    },
    []
  );

  /**
   * Clear all filters
   */
  const clearFilters = useCallback((): void => {
    setFilters({});
    setPageState(1);
  }, []);

  /**
   * Refresh the current page from scratch
   */
  const refresh = useCallback(async (): Promise<void> => {
    await fetchTransactions(page, pageSize);
  }, [fetchTransactions, page, pageSize]);

  /**
   * Manually override the normalized vendor for a single transaction.
   * Optimistic-updates local state; rolls back and toasts on error.
   * Stable callback — no dependency on the transactions array.
   */
  const updateVendor = useCallback(async (id: string, vendor: string): Promise<void> => {
    const token = sessionRef.current?.access_token;
    if (!token) return;

    // Read previous value from ref to avoid capturing transactions in the closure
    // (which would change the callback identity on every render and break memo).
    const previous = transactionsRef.current.find((t) => t.id === id)?.normalizedMerchant ?? null;

    setTransactions((prev) =>
      prev.map((t) => t.id === id ? { ...t, normalizedMerchant: vendor || null } : t),
    );

    try {
      await apiJson(
        `/api/plaid-transactions/${id}/vendor`,
        { method: 'PATCH', body: JSON.stringify({ vendor }) },
        token,
      );
    } catch {
      setTransactions((prev) =>
        prev.map((t) => t.id === id ? { ...t, normalizedMerchant: previous } : t),
      );
      toast({ title: 'Failed to save vendor', status: 'error', duration: 3000, isClosable: true });
      throw new Error('Failed to update vendor');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toast]);

  // Store fetchTransactions in a ref to avoid dependency issues
  const fetchTransactionsRef = useRef(fetchTransactions);
  fetchTransactionsRef.current = fetchTransactions;

  /**
   * Fetch whenever the page, page size, filters, sort, or tenant change.
   */
  useEffect(() => {
    if (autoFetch && hasSession && selectedTenant?.id) {
      fetchTransactionsRef.current(page, pageSize);
    }
  }, [autoFetch, hasSession, selectedTenant?.id, filters, sort, page, pageSize]);

  return {
    transactions,
    totalCount,
    page,
    pageSize,
    totalPages,
    isLoading,
    error,
    filters,
    sort,
    setPage,
    setPageSize,
    applyFilters,
    updateFilters,
    applySort,
    toggleSort,
    clearFilters,
    refresh,
    updateVendor,
  };
}

/**
 * Options for useSingleTransaction hook
 */
interface UseSingleTransactionOptions {
  transactionId: string | undefined;
}

/**
 * Return type for useSingleTransaction hook
 */
interface UseSingleTransactionReturn {
  transaction: PlaidTransaction | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

/**
 * Hook for fetching a single transaction by ID
 */
export function useSingleTransaction(
  options: UseSingleTransactionOptions
): UseSingleTransactionReturn {
  const { transactionId } = options;

  const { session } = useAuthContext();
  const sessionRef = useRef(session);
  useEffect(() => { sessionRef.current = session; });
  const { selectedTenant } = useUser();

  const [transaction, setTransaction] = useState<PlaidTransaction | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch single transaction from API
   */
  const fetchTransaction = useCallback(async (): Promise<void> => {
    if (!sessionRef.current?.access_token || !selectedTenant || !transactionId) {
      setTransaction(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data: PaginatedTransactionsResponse = await apiJson<PaginatedTransactionsResponse>(
        `/api/plaid-transactions?id=${encodeURIComponent(transactionId)}`,
        {
          headers: {
            'x-tenant-id': selectedTenant.id,
          },
        },
        sessionRef.current!.access_token
      );

      if (data.transactions.length === 0) {
        throw new Error('Transaction not found');
      }

      setTransaction(data.transactions[0]);
    } catch (err) {
      setError(toUserMessage(err, "We couldn't load the transaction. Please try again."));
      setTransaction(null);
    } finally {
      setIsLoading(false);
    }
  }, [selectedTenant, transactionId]);

  /**
   * Fetch on mount and when dependencies change
   */
  useEffect(() => {
    if (transactionId) {
      fetchTransaction();
    }
  }, [transactionId, fetchTransaction]);

  return {
    transaction,
    isLoading,
    error,
    refresh: fetchTransaction,
  };
}

export default useTransactions;
